/**
 * OptimizedImage — universal responsive image component.
 *
 * Routes the source URL through the backend `/api/img` proxy which:
 *   - downloads the original (local upload or external URL),
 *   - resizes to the requested width,
 *   - returns a WebP, cached for 1 year on disk.
 *
 * Builds a `srcSet` of 480/768/1280/1920 widths and applies sensible defaults:
 *   - `loading="lazy"`, `decoding="async"`, `fetchpriority="auto"`
 *
 * Override with `priority` (for above-the-fold hero) to eager-load.
 *
 * Usage:
 *   <OptimizedImage src={imageUrl} alt="..." className="w-full h-full object-cover"
 *                   sizes="(max-width: 768px) 100vw, 50vw" />
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WIDTHS = [480, 768, 1280, 1920];

const buildProxyUrl = (src, w, q = 80) => {
  if (!src) return "";
  return `${API}/img?url=${encodeURIComponent(src)}&w=${w}&q=${q}`;
};

const OptimizedImage = ({
  src,
  alt = "",
  className = "",
  sizes = "100vw",
  priority = false,
  width,
  height,
  quality = 80,
  onError,
  style,
  ...rest
}) => {
  if (!src) return null;

  // Already a WebP we control? Still go through the proxy so we get the right size.
  // External URLs get proxied to WebP too.
  const srcSet = WIDTHS.map((w) => `${buildProxyUrl(src, w, quality)} ${w}w`).join(", ");
  const fallback = buildProxyUrl(src, 1280, quality);

  return (
    <img
      src={fallback}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      className={className}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : "auto"}
      onError={onError}
      style={style}
      {...rest}
    />
  );
};

export default OptimizedImage;
